import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Approvers } from '../../shared/approvers/approvers';
import { VIPVendorList } from '../../shared/vendor-vip-list/vendor-vip-list';
import { VIPVendorInfo } from '../../shared/vendor-vip-info/vendor-vip-info';
import { Approver, VIP } from '../../shared/type';
import { Button, StatusIconProtoProps } from '@nike/eds';
import { useI18nContext } from 'context/language/use-i18n-context';
import ts from './i18n';
import { usePassPolicies } from 'context/PassPolicy';
import { GuestPass, PassRequest } from 'models/IPassRequest';
import { registerPass } from 'services/pass-service';
import { useUserContext } from '../../../context/user';
import { HttpStatusCode } from 'axios';
import { toUTCDate } from '../../../utils/date-helper';
import { AppConfig } from '../../../utils/constants';
import { StoreOption } from '../guest/type';
import { sortPassLocations } from 'utils/helper';
import { GetPassLocations } from 'services/pass-location.service';
import { VisitorPass } from 'models/IVisitorPass';

const vipInitValues = { email: '', firstName: '', lastName: '', weChatId: null };

const vipGuestPassDefault: GuestPass = {
  passId: null,
  sponsorId: 0,
  passTypeId: 6,
  firstName: '',
  lastName: '',
  emailAddress: '',
  shoppingDateStart: new Date(),
  shoppingDateEnd: new Date(),
  companyName: '',
  compAmount: '',
  compNumber: '',
  passLocationId: 0,
  passPolicyId: 0,
  relationshipId: null,
  weChatId: null,
  timeSlotId: null,
  govtComplianceChecked: false,
  emailLanguageGroupId: null,
  guestEmailLanguage: '',
};

const isSelectedVipEmpty = (vip: VIP) => {
  return vip.email === '' && vip.firstName === '' && vip.lastName === '';
};

type Props = {
  onDismiss: () => void;
  setSnackBar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
  vipPassToEdit: VisitorPass | null;
};

export const VIPPass: FC<Props> = ({ onDismiss, setSnackBar, vipPassToEdit }) => {
  const { i18nString } = useI18nContext();
  const passPoliciesContext = usePassPolicies();
  const { passplayUser } = useUserContext();
  const [passLocations, setPassLocations] = useState<StoreOption[]>([]);
  const [vipList, setVipList] = useState<VIP[]>();
  const [approver, setApprover] = useState<Approver>();
  const [selectedVip, setSelectedVip] = useState<VIP>(vipInitValues);

  const vipPolicy = passPoliciesContext.passPolicies.find(
    policy => policy.passTypeToken === 'PASS_TYPE_VIP',
  );

  useEffect(() => {
    if (vipPolicy) {
      GetPassLocations(vipPolicy?.passLocationGroupId).then(response => {
        const selectStore: StoreOption = {
          value: 0,
          label: i18nString(ts.DDL_STORE_SELECT),
          shoppingDays: 0,
        };
        const storeOptions = sortPassLocations(response.data);
        setPassLocations([selectStore, ...storeOptions]);
      });
    }
  }, [vipPolicy]);
  useEffect(() => {
    if (vipPassToEdit?.passId ?? 0 > 0) {
      const editVIP: VIP = {
        email: vipPassToEdit?.emailAddress ?? '',
        firstName: vipPassToEdit?.firstName ?? '',
        lastName: vipPassToEdit?.lastName ?? '',
        weChatId: vipPassToEdit?.weChatId ?? null,
      };
      setSelectedVip(editVIP);
    }
  }, [vipPassToEdit]);
  const handleApproverChange = (option: any) => {
    setApprover({ id: option.value, name: option.label });
  };

  const handleVIPAdd = (vipInfo: VIP) => {
    setVipList(prev => {
      if (prev) {
        return [...prev, vipInfo];
      } else {
        return [vipInfo];
      }
    });
    setSelectedVip(vipInitValues);
  };

  const handleVIPDelete = (vip: VIP) => {
    setVipList(prev =>
      prev?.filter(
        v => v.firstName + v.lastName + v.email !== vip.firstName + v.lastName + vip.email,
      ),
    );
  };

  const handleVIPEdit = (vip: VIP) => {
    if (isSelectedVipEmpty(selectedVip)) {
      setVipList(prev =>
        prev?.filter(v => v.firstName + v.email !== vip.firstName + v.lastName + vip.email),
      );
      setSelectedVip(vip);
    }
  };
  const handleInfoEdit = (vipInfo: VIP) => {
    if (
      (vipInfo.email === '' && vipPolicy?.emailFlag == AppConfig.emailFlags.required) ||
      vipInfo.firstName === '' ||
      vipInfo.lastName === ''
    ) {
      setVipList([]);
    } else {
      setVipList([vipInfo]);
    }
    setSelectedVip(vipInfo);
  };
  const handleRegister = async () => {
    if (vipPolicy === undefined) return;
    const vipPass: PassRequest = {
      approver: approver?.id ?? '',
      guestPasses:
        vipList?.map(
          vip =>
            ({
              ...vipGuestPassDefault,
              passId: vipPassToEdit?.passId ?? null,
              firstName: vip.firstName,
              sponsorId: passplayUser?.sponsorId ?? 0,
              lastName: vip.lastName,
              emailAddress: vip.email,
              passPolicyId: vipPolicy.passPolicyId,
              shoppingDateStart: toUTCDate(new Date(vipPolicy.shoppingStartDate)),
              shoppingDateEnd: toUTCDate(new Date(vipPolicy.shoppingEndDate)),
              passLocationId: passLocations.length > 0 ? passLocations[1].value : 0,
            }) as GuestPass,
        ) ?? ([] as GuestPass[]),
    };
    try {
      const response = await registerPass(passplayUser?.sponsorId, vipPass);
      if (response.status === HttpStatusCode.Ok) {
        setVipList([]);
        setSelectedVip(vipInitValues);
        let message = ts.TOASTR_PASS_ADDED;
        if (vipPassToEdit && vipPassToEdit.passId > 0) {
          message = ts.TOASTR_CHANGES_SAVED;
        }
        setSnackBar({
          showSnack: true,
          message: i18nString(message).replace(/\{{\w+\}}/g, 'VIP'),
          snackStatus: 'success',
        });
        onDismiss();
      } else {
        setSnackBar({
          showSnack: true,
          message: i18nString(ts.TOASTR_PASS_ERROR),
          snackStatus: 'error',
        });
      }
    } catch (e) {
      setSnackBar({
        showSnack: true,
        message: i18nString(ts.TOASTR_PASS_ERROR),
        snackStatus: 'error',
      });
      onDismiss();
    }
  };

  const disableRegister = (): boolean | undefined => {
    return (
      vipList === undefined ||
      vipList?.length == 0 ||
      approver == undefined ||
      passLocations.length === 0 ||
      (vipPolicy?.passesAllowed == 0
        ? false
        : (vipPolicy?.passesAllowed || 0) - (vipPolicy?.passesUsed || 0) == 0)
    );
  };

  return (
    <div data-testid="vip-pass" className="pass_modal_width">
      <Approvers onApproverChange={handleApproverChange} />
      <section
        className={`eds-flex eds-flex--direction-column eds-flex--justify-content-center eds-spacing--mt-24 eds-background--white section-border`}
      >
        <VIPVendorInfo
          onVIPVendorAdd={handleVIPAdd}
          onVIPVendorClear={() => setSelectedVip(vipInitValues)}
          vipVendorToEdit={selectedVip}
          emailRequired={vipPolicy?.emailFlag}
          passType={vipPolicy?.passTypeId ?? 6}
          isEdit={vipPassToEdit !== null}
          handleEdit={handleInfoEdit}
          count={vipList?.length ?? 0}
        />
        {vipPassToEdit === null && (
          <VIPVendorList
            showEmail={vipPolicy?.emailFlag !== AppConfig.emailFlags.none}
            VIPVendorList={vipList}
            handleDelete={handleVIPDelete}
            handleEdit={handleVIPEdit}
            passType={vipPolicy?.passTypeId ?? 6}
          />
        )}
      </section>
      <div className="eds-flex eds-flex--justify-content-center eds-spacing--mt-48">
        <Button
          className="txt_uppercase"
          data-testid="register"
          disabled={disableRegister()}
          size="medium"
          variant="primary"
          type="submit"
          onClick={() => handleRegister()}
        >
          {vipPassToEdit?.passId && vipPassToEdit.passId > 0
            ? i18nString(ts.PASS_MODAL_UPDATE)
            : i18nString(ts.PASS_MODAL_REGISTER)}
        </Button>
      </div>
    </div>
  );
};
